/**
 * A10A — compatibility-barrel seam. These are the base session / space /
 * entity-preview primitives that used to live at the top of the red
 * `editor.ts` (3642 lines). Extracted verbatim (behaviour-preserving move,
 * no redesign) so `editor.ts` can shrink; every symbol here is re-exported
 * from `./editor` so no external import path changes and every moved
 * symbol keeps its EXACT original type identity (re-export, not a
 * redeclaration).
 */
import { z } from "zod";

// Exported (not just module-local) so non-schema call sites that must
// reproduce a `.refine()` threshold exactly — e.g. `gripModeTransform.ts`'s
// pre-dispatch MIRROR degenerate-axis guard, which mirrors the
// `geometry/mirror-entities` `.refine()` below — import the SAME constant
// instead of duplicating the literal and risking silent drift.
export const FLOAT_EPSILON = 1e-12;

export const bootstrapAbsoluteAdminEmail = "cosccc@gmail.com";

export const userRoleSchema = z.enum([
  "absolute_admin",
  "admin",
  "owner",
  "editor",
  "commenter",
  "viewer",
]);

export const accessStatusSchema = z.enum(["active", "blocked", "suspended"]);

export const commandSourceSchema = z.enum([
  "toolbar",
  "commandline",
  "scene",
  "session",
  "system",
]);

export const matchpropModeSchema = z.enum(["basic", "special", "all"]);

export const panelSchema = z.enum(["overview", "properties", "history", "ai"]);

export const layoutContextSchema = z.enum(["model", "paper", "viewport"]);

export const spaceKindSchema = z.enum(["model", "layout"]);

export const entityTypeSchema = z.enum([
  "Block",
  "Hatch",
  "Rectangular Associative Array",
  "Line",
  "Point",
  "Circle",
  "Arc",
  "Ellipse",
  "Spline",
  "LWPOLYLINE",
  "Text",
  "MText",
  "Dimension",
  "Multileader",
  "Revision Cloud",
  "Wipeout",
  "Viewport",
  // Legacy heavy POLYLINE (AcDb2dPolyline / AcDb3dPolyline / AcDbPolygonMesh /
  // AcDbPolyFaceMesh). Distinct from "LWPOLYLINE" because it is a different
  // AutoCAD object, not a second name for the lightweight one — see
  // `polylineEntitySchema` in drawing-model for the enumerated differences.
  "Polyline",
  // ACAD_TABLE. Rendered through the anonymous `*T<N>` block that carries the
  // table graphics; cell/style editing is a separate feature.
  "Table",
  // OLE2FRAME. Rendered as its frame rectangle — AutoCAD's own documented
  // behaviour when the OLE server cannot draw the object.
  "OLE Frame",
]);

export const sessionUserSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1),
  role: userRoleSchema,
  status: accessStatusSchema,
});

export const viewportSnapshotSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  scale: z.string().min(1),
  status: z.enum(["active", "locked", "planned"]),
  clipped: z.boolean(),
  overrideLayerCount: z.number().int().nonnegative(),
});

export const drawingSpaceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: spaceKindSchema,
  pageSetup: z.string().min(1),
  viewports: z.array(viewportSnapshotSchema),
});

export const entityPreviewSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: entityTypeSchema,
  spaceId: z.string().min(1),
  summary: z.string().min(1),
  editableProperties: z.array(z.string().min(1)).min(1),
  properties: z.record(z.string(), z.string()),
});

export const propertyValueMapSchema = z.record(z.string(), z.string());

export const transcriptEntrySchema = z.object({
  id: z.string().min(1),
  // ТЗ §14 — a failed command / parse error renders a red "error" transcript
  // row AND projects to a `status:"error"` telemetry record (see
  // `telemetryToneSchema` / `toneToStatus`). Kept aligned with the telemetry
  // tone domain so a transcript tone can never project to an unmapped status.
  tone: z.enum(["info", "success", "warning", "error"]),
  message: z.string().min(1),
});
